import React from "react";
import PropTypes from "prop-types";
import {
  Button
} from "@sainsburys-tech/fable";
import { LoadingIndicator } from "@sainsburys-tech/fable";
import { AlertCircleFilled } from '@sainsburys-tech/icons'
import { withToastManager } from "../WithToastManager";
import * as scheduleStatus from "../../model/store-commissioning/scheduleStatusKeys";
import apiProxy from "../../api/apiProxy.js";
import { Event } from "../Tracking";

class SendScheduleButton extends React.Component {
  state = {
    waiting: false,
    terminalState: false,
    successMessage: this.props.successMessage,
    pendingMessage: this.props.pendingMessage,
    sentMessage: this.props.sentMessage
  };

  componentWillUnmount = () => {
    clearInterval(this.intervalId);
    clearTimeout(this.timeoutId);
  };

  // 5 seconds
  pollInterval = 5000;

  // 60 seconds
  waitTimeout = 60000;

  intervalId = 0;

  timeoutId = 0;

  handleClick = () => {
    this.props.onClick(this.props.pendingMessage);
    Event("Commissioning Event", "Test Trading", "(not set)");

    if (this.props.onDisableButtons) {
      this.props.onDisableButtons();
    }

    this.setState({
      waiting: true,
      terminalState: false,
      successMessage: this.props.successMessage,
      pendingMessage: this.props.pendingMessage,
      sentMessage: this.props.sentMessage
    });

    this.props.sendSchedule().then(
      scheduleId => {
        this.intervalId = setInterval(
          () => this.handlePoll(scheduleId),
          this.pollInterval
        );
        this.timeoutId = setTimeout(
          () => this.handleTimeout(),
          this.waitTimeout
        );
      },
      () => {
        this.handleTerminalState(
          false,
          "This action could not be completed. Please try again."
        );
      }
    );
  };

  handlePoll = trackingId => {
    apiProxy.storeLighting.getSchedule(trackingId).then(r => {
      const statusCode = r.status.code;

      switch (statusCode) {
        case scheduleStatus.PENDING:
          this.handleUpdateStatus(this.state.pendingMessage);
          break;
        case scheduleStatus.SENT:
          this.handleUpdateStatus(this.state.sentMessage);
          break;
        case scheduleStatus.ACKNOWLEDGED:
          this.handleTerminalState(true, this.state.successMessage);
          break;
        case scheduleStatus.FAILED:
        case scheduleStatus.CANCELED:
          this.handleTerminalState(
            false,
            "This action could not be completed. Please try again."
          );
          break;
        default:
          break;
      }
    });
  };

  handleTimeout = () => {
    this.props.toastManager.add(
      "warning",
      "This is taking a while...",
      `It looks like things are taking a little longer than expected.
      We recommend you do not refresh the page.`
    );
  };

  handleUpdateStatus = status => {
    if (this.state.waiting && this.props.status !== status) {
      this.props.onStatusChange(status);
    }
  };

  handleTerminalState = (success, statusMessage) => {
    clearInterval(this.intervalId);
    clearTimeout(this.timeoutId);

    this.props.onStatusChange(statusMessage);

    this.setState(
      {
        waiting: false,
        terminalState: true,
        hasFailed: !success
      },
      () => {
        if (this.props.onEnableButtons) {
          this.props.onEnableButtons();
        }

        if (success) {
          if (this.props.onSuccess) {
            this.props.onSuccess(statusMessage);
          }
        } else {
          if (this.props.onFail) {
            this.props.onFail(statusMessage);
          }
        }
      }
    );
  };

  render() {
    return (
      <React.Fragment>
        {this.props.hasFailed && (
          <div className="fable:my-2">
            <AlertCircleFilled className="cb-c-send-schedule-button__error-icon" />
            <b>Oops! Something's gone wrong.</b>
            <br />
            {this.props.status}
          </div>
        )}
        {this.props.outlined ? (
            <Button
              variant="secondary"
              onClick={this.handleClick}
              className="fable:mr-4"
              disabled={this.props.disabled || this.state.waiting}
            >
              {this.props.label}
            </Button>
          ) : (
            <Button
              onClick={this.handleClick}
              className="fable:mr-4"
              disabled={this.props.disabled || this.state.waiting}
            >
              {this.props.label}
            </Button>
          )}
          <React.Fragment>
            {this.state.waiting && this.props.showSpinner && (
              <LoadingIndicator
                isLoading
                isStandalone 
                size="lg"
                shouldPreventFocus
                className="fable:mr-4 fable:inline"
              />
            )}
            {!this.props.hasFailed && this.props.status}
          </React.Fragment>
      </React.Fragment>
    );
  }
}

SendScheduleButton.propTypes = {
  label: PropTypes.string.isRequired,
  sendSchedule: PropTypes.func.isRequired,
  successMessage: PropTypes.string.isRequired,
  pendingMessage: PropTypes.string.isRequired,
  sentMessage: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  disabled: PropTypes.bool,
  onDisableButtons: PropTypes.func,
  onEnableButtons: PropTypes.func,
  outlined: PropTypes.bool,
  status: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  showSpinner: PropTypes.bool,
  hasFailed: PropTypes.bool,
  onFail: PropTypes.func,
  onClick: PropTypes.func.isRequired
};

SendScheduleButton.defaultProps = {
  disabled: false,
  successMessage: "Acknowledged",
  pendingMessage: "Pending",
  sentMessage: "Sent",
  status: "",
  showSpinner: true
};

export default withToastManager(SendScheduleButton);
